import {createStore} from "zustand/vanilla";
import {immer} from "zustand/middleware/immer";
import {useStore} from "zustand";

interface Car {
  driverId: string | null,
  driverBirthDate: Date,
  driverHasCreditCard: boolean,
  bookedEquipments: string[],
  fieldsWithError: Record<string, boolean>,
  isEditing: boolean
}

interface CarRentalServiceState {
  cars: Record<string, Car>,
  editingCarsList: string[]
}

interface CarRentalServiceStoreActions {
  startEditingCar: (carId: string, carState: Car) => void;
  endEditingCar: (carId: string) => void;
  endEditingCarsExcept: (carIds: string[]) => void
  setDriverId: (carId: string, driverId: string | null) => void;
  setDriverBirthDate: (carId: string, birthDate: Date) => void;
  setDriverHasCreditCard: (carId: string, hasCreditCard: boolean) => void;
  toggleEquipmentEnquiry: (carId: string, equipmentId: string) => void
  setFormFieldError: (carId: string, fieldName: string) => void
  unsetFormFieldError: (carId: string, fieldName: string) => void
}

type CarRentalServiceStore = CarRentalServiceState & CarRentalServiceStoreActions

export const createCarRentalServiceStore = (initState: CarRentalServiceState) =>
  createStore<CarRentalServiceStore>()(
    immer((set) => ({
      ...initState,
      startEditingCar(carId, carState) {
        set(({cars, editingCarsList}) => {
          cars[carId] = carState
          editingCarsList.push(carId)
        })
      },
      endEditingCar(carId) {
        set((state) => {
          state.editingCarsList = state.editingCarsList.filter(
            (id) => id !== carId
          )
        })
      },
      endEditingCarsExcept(carIds) {
        set((state) => {
          state.editingCarsList = state.editingCarsList.filter(
            (id) => carIds.includes(id)
          )
        })
      },
      setDriverId(carId, driverId) {
        set(({cars}) => {
          if (!cars[carId]) return
          cars[carId].driverId = driverId
        })
      },
      setDriverBirthDate(carId, birthDate) {
        set(({cars}) => {
          if (!cars[carId]) return
          cars[carId].driverBirthDate = birthDate
        })
      },
      setDriverHasCreditCard(carId, hasCreditCard) {
        set(({cars}) => {
          if (!cars[carId]) return
          cars[carId].driverHasCreditCard = hasCreditCard
        })
      },
      toggleEquipmentEnquiry(carId, equipmentId) {
        set(({cars}) => {
          if (!cars[carId]) return
          if (cars[carId].bookedEquipments.includes(equipmentId)) {
            cars[carId].bookedEquipments = cars[carId].bookedEquipments.filter((id) => id !== equipmentId)
            return
          }

          cars[carId].bookedEquipments.push(equipmentId)
        })
      },
      setFormFieldError(carId, fieldName) {
        set(({cars}) => {
          if (!cars[carId]) return
          cars[carId].fieldsWithError[fieldName] = true
        })
      },
      unsetFormFieldError(carId, fieldName) {
        set(({cars}) => {
          if (!cars[carId]) return
          cars[carId].fieldsWithError[fieldName] = false
        })
      }
    }))
  );

export const isValidForm = (carId: string) => ({ cars }: CarRentalServiceState) => {
  return !Object.values(cars[carId]?.fieldsWithError ?? {})
    .some((hasError) => hasError)
}

export const editingCar = (carId: string) => ({ cars }: CarRentalServiceState) => {
  return cars[carId]
}

export const carIsBeingEdited = (carId: string) => ({ editingCarsList }: CarRentalServiceState) => {
  return editingCarsList.includes(carId)
}

export const carCouldHaveHigherPrice = (carId: string, maxAge: Date, minAge: Date) => ({ cars }: CarRentalServiceState) => {
  const birthdate = cars[carId]?.driverBirthDate
  if (!birthdate) return false
  return !(birthdate >= maxAge && birthdate <= minAge);
}

export const carRentalServiceStore = createCarRentalServiceStore({
  cars: {},
  editingCarsList: []
})

type StoreSelector<T, S> = (state: S) => T
interface UseBoundStore<S> {
  (): S,
  <T>(selector: StoreSelector<T, S>): T
}

export const useCarRentalServiceStore: UseBoundStore<CarRentalServiceStore> = <T>(selector?: StoreSelector<T, CarRentalServiceStore>) => {
  const store = useStore(carRentalServiceStore);

  if(!selector) {
    return store;
  }

  return selector(store);
}
